import { map } from 'lodash/fp';
import { useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';

import { MessageType } from 'dashboard/lib/interfaces/Message';
import ApiResponseError from 'lib/errors/ApiResponseError';
import { rtkErrorHandlerLogic, UseRTKError } from 'lib/hooks/useRTKErrorHandler';

export type QueryResult = {
  isLoading: boolean;
  isFetching: boolean;
  requestId?: string;
  error?: UseRTKError;
};

interface UseBatchQueryArgs {
  queries: QueryResult[];
  onError: ({
    error,
    requestId,
  }: {
    error: ApiResponseError;
    requestId?: string;
  }) => { message: string; level?: MessageType } | undefined;
}

/**
 * A hook that takes in multiple RTK query hook results and manages overall loading state + error handling.
 * Requires each RTK query to have a unique key.
 */
export default ({ queries, onError }: UseBatchQueryArgs) => {
  const allIsLoadings = map('isLoading', Object.values(queries));
  const allIsFetchings = map('isFetching', Object.values(queries));
  const allErrors = map('error', queries);
  const allIds = map('requestId', queries);

  const memoIsLoadings = useMemo(() => allIsLoadings, allIsLoadings);
  const memoIsFetchings = useMemo(() => allIsFetchings, allIsFetchings);
  const memoErrors = useMemo(() => allErrors, allErrors);
  const memoIds = useMemo(() => allIds, allIds);

  const dispatch = useDispatch();

  useEffect(() => {
    memoErrors.forEach((error, i) => {
      rtkErrorHandlerLogic(error, dispatch, (error) => onError({ error, requestId: memoIds[i] }));
    });
  }, [memoErrors, memoIds]);

  return {
    isLoadingAll: memoIsLoadings.some(Boolean),
    isFetchingAll: memoIsFetchings.some(Boolean),
  };
};
